import type maplibregl from "maplibre-gl";
import { RasterTileSource } from "maplibre-gl";

// types.ts
export namespace MapLibre {
  export type BackgroundLayer = maplibregl.BackgroundLayerSpecification;
  export type FillLayer = maplibregl.FillLayerSpecification;
  export type LineLayer = maplibregl.LineLayerSpecification;
  export type SymbolLayer = maplibregl.SymbolLayerSpecification;
  export type RasterLayer = maplibregl.RasterLayerSpecification;
  export type CircleLayer = maplibregl.CircleLayerSpecification;
  export type FillExtrusionLayer = maplibregl.FillExtrusionLayerSpecification;
  export type HeatmapLayer = maplibregl.HeatmapLayerSpecification;
  export type HillshadeLayer = maplibregl.HillshadeLayerSpecification;
}
type InlineSource = {
  type: "geojson";
  data: any;
  buffer?: number;
  tolerance?: number;
  cluster?: boolean;
  clusterMaxZoom?: number;
  clusterRadius?: number;
  maxzoom?: number;
};

/**
 * App-level properties attached to dynamic CM Table layer specs.
 * These are not part of the base MapLibre type specs.
 */
type CmLayerExtras = {
  initialVisibility?: boolean;
  disableClicking?: boolean;
};

export type ExtendedFillLayer = Omit<MapLibre.FillLayer, "source"> &
  CmLayerExtras & {
    source: string | InlineSource;
  };

export type ExtendedLineLayer = Omit<MapLibre.LineLayer, "source"> &
  CmLayerExtras & {
    source: string | InlineSource;
  };

export type ExtendedSymbolLayer = Omit<MapLibre.SymbolLayer, "source"> &
  CmLayerExtras & {
    source: string | InlineSource;
  };

export type ExtendedRasterLayer = Omit<MapLibre.RasterLayer, "source"> &
  CmLayerExtras & {
    source: string | RasterTileSource;
  };

/** Extended circle layer — used by CM Table point features */
export type ExtendedCircleLayer = Omit<MapLibre.CircleLayer, "source"> &
  CmLayerExtras & {
    source: string | InlineSource;
  };

export type MapLibreLayerSpecification =
  | ExtendedFillLayer
  | ExtendedLineLayer
  | ExtendedSymbolLayer
  | ExtendedRasterLayer
  | ExtendedCircleLayer;

interface BaseLayerConfig {
  id: string;
  type: string;
  url: string;
  useBasicAuth?: boolean;
  useOAuth?: boolean;
  initialVisibility: boolean;
  disableClicking?: boolean;
  disablePopups?: boolean;
  showFiltersOnClick?: boolean;
  searchable?: boolean;
  searchableFields?: string[];
  searchApiEndpoint?: string;
  queryParams?: { [key: string]: string };
  joinedData?: {
    name: string;
    url: string;
    templateFields: {
      parentField: string;
      parentTargetTable?: string;
    };
    enableFilters?: boolean;
    filters?: {
      [key: string]: string;
    };
  }[];
  entityMapping?: {
    table: string;
  };
  error?: boolean;
}

// Search result interface for the map search functionality
export interface SearchResult {
  id: string | number;
  name: string;
  type: string; // 'coordinate', 'place', etc. - matches layer ID
  coordinates?: [number, number]; // [lng, lat]
  properties?: Record<string, any>;
  layerId?: string; // Reference to the layer it belongs to
}

export interface RasterDemLayerConfig extends BaseLayerConfig {
  type: "raster-dem";
  options: {
    minZoom: number;
    maxZoom: number;
    tileSize: number;
    exaggeration: number;
  };
}

export interface GeoJsonLayerConfig extends BaseLayerConfig {
  type: "geojson";
  metadata?: {
    isFulcrum?: boolean;
  };
  geometry: "circle" | "line" | "fill" | "symbol"; // Specifies how the GeoJSON data should be rendered
  options?: {
    minZoom?: number;
    maxZoom?: number;
  };
  defaultIcon: string; // Only required if geometry === "symbol"
  styleByField?: string; // Field to use to apply colour ramps
  filters?: {
    type: string;
    db_table: string;
    field: string;
  }[];
  icons?: string[]; // Only required if geometry === "symbol"
  popupFields?: string[]; // Fields to show in popup
  descriptionField?: string; // Field to use for description
  style: {
    // Specific to line type layers
    "line-color"?: string;
    "line-width"?: number;
    // Specific to circle type layers
    "circle-radius"?: number;
    "circle-color"?: string;
    "circle-stroke-width"?: number;
    "circle-stroke-color"?: string;
    // For labels
    labelField?: string;
    // Label properties can now be either static values or zoom-dependent expressions
    // e.g., ["step", ["zoom"], 0, 10, 12, 14, 16] - invisible at zoom < 10, size 12 at zoom 10-14, size 16 at zoom > 14
    labelSize?: number | any[];
    labelColor?: string | any[];
    labelFont?: string[] | any[];
    labelOffset?: [number, number] | any[];
    labelOpacity?: number | any[]; // Control label opacity based on zoom
    labelHaloColor?: string | any[]; // Text outline color
    labelHaloWidth?: number | any[]; // Text outline width
    iconSize?: number; // Only required if geometry === "symbol
    iconStyle?: any;
    opacity?: number;
  };
}

export interface RasterLayerConfig extends BaseLayerConfig {
  type: "raster";
  options: {
    tileSize: number;
    opacity: number;
  };
  /**
   * Optional override for the WMS `GetLegendGraphic` URL rendered by the legend
   * overlay. Use this when the server's default legend (20x20 cells built from the
   * layer name) is wrong for the specific sub-layer — e.g. raster ramps whose
   * GetCapabilities `<LegendURL>` advertises a native size like 38x595. When
   * present, the overlay uses this URL verbatim instead of composing one from
   * `queryParams`.
   */
  legendUrl?: string;
}

// After RasterLayerConfig, add a new interface for Fulcrum layers
export interface FulcrumLayerConfig extends Omit<GeoJsonLayerConfig, "type"> {
  type: "fulcrum";
  formId: string;
  region?: "US" | "AU" | "EU";
}

// Retained for backward compatibility — config.json now uses type:"geojson" with the /api/v2/media/geojson/ endpoint (Path A).
export interface MediaGeotaggedLayerConfig extends Omit<GeoJsonLayerConfig, "type"> {
  type: "media-geotagged";
}

export interface CogLayerConfig extends BaseLayerConfig {
  type: "cog";
  bounds?: [[number, number], [number, number]];
  options?: {
    minZoom?: number;
    maxZoom?: number;
    tileSize?: number;
    opacity?: number;
  };
}

export interface PMTilesLayerConfig extends BaseLayerConfig {
  type: "pmtiles";
  layerFormat?: "vector" | "raster";
  sourceLayer?: string;
  style?: any;
  bounds?: [[number, number], [number, number]];
  options?: {
    minZoom?: number;
    maxZoom?: number;
    tileSize?: number;
    opacity?: number;
  };
}

// Update the MapLayerConfig type to include FulcrumLayerConfig
export type MapLayerConfig =
  | RasterDemLayerConfig
  | GeoJsonLayerConfig
  | RasterLayerConfig
  | FulcrumLayerConfig
  | MediaGeotaggedLayerConfig
  | PMTilesLayerConfig
  | CogLayerConfig;

// Update LegendLayerConfig to include FulcrumLayerConfig too
export type LegendLayerConfig =
  | GeoJsonLayerConfig
  | RasterLayerConfig
  | FulcrumLayerConfig
  | MediaGeotaggedLayerConfig
  | PMTilesLayerConfig
  | CogLayerConfig;

// Data Source Adapter Pattern Types (100% backwards compatible)
export interface FieldMapping {
  id: string | string[]; // Field path(s) for ID
  name: string | string[]; // Field path(s) for name
  description?: string | string[]; // Field path(s) for description
  thumbnail?: string | string[];
  created_at?: string | string[];
  last_updated_at?: string | string[];
  url?: string | string[];
  media_type?: string | string[];
  geometry?: string | string[];
  coordinates?: string | string[];

  // Custom transformation functions
  transformers?: {
    [field: string]: (value: any, originalData: any) => any;
  };
}

export interface StandardizedDataItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description: string | null;

  // Optional common fields
  thumbnail?: string;
  created_at?: string;
  last_updated_at?: string;

  // Primary image (for entities with photos)
  primary_image?: {
    id: string | number;
    url: string;
    thumbnail: string;
    name?: string;
    description?: string;
  };

  // Media-specific
  url?: string;
  media_type?: string;
  embed_url?: string;
  original_file?: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };
  remote_details?: Record<string, any>;

  // Geographic
  geometry?: string | object;
  coordinates?: [number, number];

  // Relationships (standardized format)
  relationships?: RelationshipsResponse;

  // Normalized relations
  relations?: INormalisedRelations;

  // Plain `restrictions: [{id, name}]` field on CM table rows / media items —
  // not part of `relationships`/`relations`, PATCHed directly as `restrictions: number[]`.
  restrictions?: { id: number | string; name: string }[];

  // Fulcrum-specific data (optional)
  fulcrum_data?: {
    form_id: string;
    created_by?: string;
    updated_by?: string;
    form_values: Record<string, any>;
    photos?: any[];
    mapped_fields: Record<string, any>;
    // Store full media objects here for MediaDrawer to access
    fullMediaObjects?: any[];
  };

  // Source metadata for debugging
  _source: {
    type: "django" | "fulcrum" | "external";
    originalId: string | number;
    rawData: any;
  };
}

// Adapter function signature - now async for relationship resolution
export type DataSourceAdapter = (
  rawData: any,
  fieldMapping?: FieldMapping,
  apiEndpoints?: ApiEndpoint[]
) => Promise<StandardizedDataItem[]>;

export interface ICmTableFieldDef {
  name: string;
  type: "string" | "number" | "boolean" | "integer";
  format?: "textarea" | "uri" | "date" | "date-time" | "email";
  label?: string;
  required?: boolean;
  enum?: (string | number)[];
  description?: string;
}

export interface ApiEndpoint {
  name: string;
  tableName: string;
  relationshipKey: string;
  queryParam?: string;
  /** @deprecated Now we use `row.relations.byTableSlug[endpoint.tableName]` for CM Table endpoints. Will be removed once all data sources are migrated to CM Tables. */
  returnedRelationshipsKey?: string;
  api_endpoint: string;
  description: string;
  entityType?: "geo" | "media" | "related" | "spatial-dataset" | "table";
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  formId?: string; // Fulcrum form ID for Fulcrum data sources
  mapLayerId?: string; // ID of the corresponding map layer (if different from tableName)
  tableSlug?: string; // Explicit slug for CM Table endpoints (prevents overloading tableName)

  // New optional fields for data source adapter pattern (100% backwards compatible)
  dataSourceType?: "django" | "fulcrum" | "external" | "cm-table"; // defaults to 'django' if not specified
  isEditable?: boolean; // defaults to true for django, false for external sources
  dataSourceConfig?: {
    fieldMapping?: FieldMapping;
    customTransformers?: Record<string, (value: any, originalData: any) => any>;
  };
  geometryType?: "Point" | "Polygon" | "LineString"; // Type of geometry this endpoint handles
  requiresGeometry?: boolean; // Whether geometry is required for creating new items
  reverseRelationships?: {
    sourceFormId: string;
    sourceFormName: string;
    recordLinkFieldId: string;
    recordLinkFieldLabel: string;
    relationshipKey: string;
    displayName: string;
  }[]; // Configuration for reverse relationships (records that link TO this endpoint)

  // Date configuration for timeline/calendar view support
  dateConfig?: {
    hasDateFilter: boolean;
    dateField?: string; // Single date field
    startDateField?: string; // Start date field for date ranges
    endDateField?: string; // End date field for date ranges
    showCalendarByDefault?: boolean; // Auto-show timeline when this endpoint is selected
  };

  // CM Table Schema Fields
  spatialType?: "point" | "multipoint" | "polygon" | "multipolygon" | "linestring" | "multilinestring" | "none";
  style?: Record<string, any>; // MapLibre paint overrides (e.g. "line-color", "line-width")
  fields?: ICmTableFieldDef[]; // Schema definition for row_data fields
}

export interface Dashboard {
  name: string;
  url: string;
}

interface MediaUploadConfig {
  service?: "imagekit" | "s3";
}

export interface Config {
  /** Present only on the database-driven config from `GET /api/v2/config/`; absent for legacy static config. */
  schema_version?: string;
  appTitle: string;
  logoPath: string;
  loginImagePath: string;
  loginMessage: string;
  colorScheme: {
    primary: string;
    secondary: string;
  };
  sidebarOpen: boolean;
  primaryFilterLayer: string;
  mapLayers: MapLayerConfig[];
  apiEndpoints: ApiEndpoint[];
  homePageShortcuts: Array<{
    name: string;
    targetTab: number;
    path?: string;
    options: { tableName?: string };
  }>;
  homePageTitle: string;
  homePageDescription: string;
  initialMapView: {
    longitude: number;
    latitude: number;
    zoom: number;
    pitch: number;
  };
  dashboardUrl?: string; // Keep for backward compatibility
  dashboardUrls?: Dashboard[]; // Keep for backward compatibility
  toolsUrls?: Dashboard[]; // New preferred naming for external tools
  defaultDataTable?: string;
  showMediaInRelatedItemsCloud?: boolean;
  showGeoInRelatedItemsCloud?: boolean;
  showIndirectRelationshipsInRing?: boolean;
  terrainExaggeration?: number; // Added for 3D terrain control
  backgroundOpacity?: number; // Controls page background overlay opacity (0.0 to 1.0)
  mediaUpload?: MediaUploadConfig;
  enableFulcrumEditing?: boolean; // Global flag to enable editing of Fulcrum data
  hideCardsWithoutImages?: boolean; // Hide cards that don't have primary images in DataGrid
  limitDataDrawerToExtent?: boolean; // When true, data drawer sorts visible-on-map items first and greys out the rest
  alwaysRequestRelatedItemsAndMedia?: boolean; // Always load Level 3 (relationships & media) instead of progressive loading
  /** When true, map camera may fit bounds to all highlighted related items; when false/omitted, only the selected entity geometry is used. */
  enableZoomToRelatedItems?: boolean;
  enableServerSearch?: boolean; // Enable/disable server search functionality
  autoExpandAdditionalDetailsWhenNoImage?: boolean; // Auto-expand Additional Details when items have no primary image or thumbnail
  auth?: {
    useCloudFront: boolean;
    endpoint: string;
    cloudFrontEndpoint: string;
  };
  // Dashboards tab configuration
  dashboardsTab?: {
    enabled?: boolean; // Default true for backward compatibility
    label?: string; // Default "Tools"
  };
  tiltMapOnPointItemSelect?: boolean;
  /**
   * Tabular (CSV → Parquet / Power BI) datasets panel config from `GET /api/v2/config/`.
   * When present, a dedicated "Tabular data" tab is rendered that lists SpatialDatasets
   * with `dataset_type='tabular'` (never on the map). See TABULAR_DATASET_FRONTEND.md.
   */
  tabularData?: {
    name: string;
    /** e.g. "/api/v2/spatial-datasets/?dataset_type=tabular" */
    listEndpoint: string;
    /** e.g. "/api/v2/powerbi/csv/" — consumed as `${powerbiPull}?dataset_id=<id>` */
    powerbiPull: string;
    /**
     * e.g. "/api/v2/csv-data/" — paginated CSV-as-JSON rows, consumed as
     * `${csvData}?file_key=<original_file_key>&limit=100&offset=0`. Uses the normal
     * CultureMap login Bearer token (NOT a data-access token). Optional; defaults to
     * "/api/v2/csv-data/" when the backend config omits it.
     */
    csvData?: string;
  };
}

// Base interface for all items
export interface BaseDataItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description: string;
  thumbnail?: string;
  created_at?: string;
  last_updated_at?: string;
  relationships?: RelationshipsResponse;
  relations?: INormalisedRelations;
  geometry?: any;
}

// Base interface for URLs that might be presigned
interface PresignedUrlInfo {
  presigned?: boolean;
  presigned_expiry?: number; // Unix timestamp
}

// Media specific fields
export interface MediaItem extends BaseDataItem, PresignedUrlInfo {
  url: string;
  thumbnail: string;
  media_type: string;
  is_primary?: boolean;

  // File metadata
  original_file: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };

  // Remote service details (without presigned info)
  remote_details?: {
    service: "imagekit" | "s3";
    url: string; // Original, non-presigned URL
    thumbnail: string; // Original, non-presigned URL
    id: string;
    mime: string;
    bucket?: string; // For S3
    key?: string; // For S3
  };
}

// Primary image interface used by other entities
export interface PrimaryImage extends PresignedUrlInfo {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  url: string | null;
  thumbnail: string | null;
  name?: string;
  description?: string;
}

// Specific types (exported)
export interface EntityItem extends BaseDataItem {
  primary_image?: {
    url?: string | null;
    thumbnail?: string | null;
  };
}

export interface GeoItem extends EntityItem {
  geometry: string;
}

// The main union type for use in components
export type FlexibleDataItem = EntityItem | MediaItem | GeoItem;

// Helper functions
export function getThumbnail(item: FlexibleDataItem): string {
  // If it's a media item with direct thumbnail
  if ("thumbnail" in item && item.thumbnail) {
    return item.thumbnail;
  }

  // If it has primary_image
  if ("primary_image" in item && item.primary_image?.thumbnail) {
    return item.primary_image.thumbnail;
  }

  // Default placeholder
  return "/icons/placeholder.svg";
}

export function getUrl(item: FlexibleDataItem): string | null {
  if ("url" in item) {
    return item.url;
  }
  if (item.primary_image?.url !== undefined) {
    return item.primary_image.url;
  }
  return null;
}

export interface HomePageShortcut {
  name: string;
  targetTab: number;
  path?: string;
}

export interface TaggedItem {
  id: number;
  name: string;
  description?: string;
  is_primary?: boolean;
}

export interface Option {
  id: number;
  name: string;
  description?: string;
  thumbnail?: string;
  is_primary?: boolean;
}

export interface OriginalFileDetails {
  filename: string;
  type: string; // MIME type
  size: string; // in kb
  hashmd5: string;
}

export interface GeoJSONPosition {
  0: number;
  1: number;
  2?: number;
}

export interface GeoJSONPoint {
  type: "Point";
  coordinates: GeoJSONPosition;
}

export interface GeoJSONMultiPoint {
  type: "MultiPoint";
  coordinates: GeoJSONPosition[];
}

export interface GeoJSONLineString {
  type: "LineString";
  coordinates: GeoJSONPosition[];
}

export interface GeoJSONMultiLineString {
  type: "MultiLineString";
  coordinates: GeoJSONPosition[][];
}

export interface GeoJSONPolygon {
  type: "Polygon";
  coordinates: GeoJSONPosition[][];
}

export interface GeoJSONMultiPolygon {
  type: "MultiPolygon";
  coordinates: GeoJSONPosition[][][];
}

export interface GeoJSONGeometryCollection {
  type: "GeometryCollection";
  geometries: GeoJSONGeometry[];
}

export type GeoJSONGeometry =
  | GeoJSONPoint
  | GeoJSONMultiPoint
  | GeoJSONLineString
  | GeoJSONMultiLineString
  | GeoJSONPolygon
  | GeoJSONMultiPolygon
  | GeoJSONGeometryCollection;

export interface GeoJSONFeature {
  type: "Feature";
  geometry: GeoJSONGeometry;
  properties: {
    name: string;
    [key: string]: any;
  };
}

export interface PreviewMapData {
  areas: GeoJSONFeature[];
  points: GeoJSONFeature[];
}

export namespace CultureMapAPI {
  // Get relationship types from config's apiEndpoints
  export type RelationshipType = string; // or more specifically: ApiEndpoint['relationshipKey']

  export interface MediaRelationship {
    id: number;
    type: RelationshipType;
    description: string;
    is_primary: boolean;
  }

  // TODO: Consider renaming this to RelationshipsPostShape across the codebase
  export interface Relationships {
    places?: RelatedEntityItem[];
    family_areas?: RelatedEntityItem[];
    persons?: RelatedEntityItem[];
    collections?: RelatedEntityItem[];
    languages?: RelatedEntityItem[];
    partners?: RelatedEntityItem[];
    plant_animals?: RelatedEntityItem[];
    simple_tags?: RelatedEntityItem[];
    special_things?: RelatedEntityItem[];
    trips?: RelatedEntityItem[];
  }

  export interface RelationshipInput {
    id: number;
    type: string;
    rel_description: string;
    is_primary?: boolean;
  }

  export interface MediaFile {
    id: number;
    url: string;
    name: string;
    type: string;
    remote_details?: RemoteDetails;
  }

  export interface MediaItem {
    id: number;
    name: string;
    description: string;
    user: number;
    organisation?: number;
    created_at: string;
    last_updated_at: string;
    is_primary: boolean | null;
    file: MediaFile;
    relationships?: Relationships;
  }

  // Other API interfaces remain the same
  export interface User {
    id: number;
    username: string;
  }

  interface PrimaryImage {
    id: number;
    url: string;
    name: string;
    description: string;
  }

  export interface BaseItem {
    id: number;
    name: string;
    description: string;
    user: number;
    organisation?: number;
    created_at: string;
    last_updated_at: string;
    primary_image?: PrimaryImage;
    relationships?: {
      [key: string]: RelatedEntityItem[];
    };
  }
}

export interface RawMediaItem {
  id: string;
  name: string;
  description: string;
  file: CultureMapAPI.MediaFile;
  type?: string;
  country_areas?: string[];
  original_filename?: string;
  relationships?: {
    [key: string]: Array<{
      id: number;
      name: string;
      description?: string;
      is_primary?: boolean;
    }>;
  };
}

export interface UploadResult {
  file: File;
  url?: string;
  error?: string;
  uploadType: "imagekit" | "s3";
  remoteDetails?: RemoteDetails;
}

export type FileProgressStage = "pending" | "hashing" | "presigning" | "uploading" | "processing" | "server" | "completed" | "failed";

export interface FileProgress {
  stage: FileProgressStage;
  completed: number;
  total: number;
  progress: number;
}

export interface UploadProgressBarsProps {
  progress: Record<string, FileProgress>;
  items: MediaItemDetailed[];
  onComplete?: () => void;
  onFailure?: () => void;
  onCancel?: () => void;
  isFinish?: boolean;
  isFailed?: boolean;
}

export interface RemoteDetails {
  service: "imagekit" | "s3";
  url: string;
  thumbnail: string;
  id: string;
  mime: string;
  bucket?: string; // For S3
  key?: string; // For S3
}

export interface RelatedEntityItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name?: string; // For server inconsistency
  description?: string; // for server inconsistency
  rel_description?: string;
  is_primary?: boolean;
  relationship_id?: number;
  entity_name?: string;
  entity_description?: string;
  entity_thumbnail?: string | null;
  category?: string;
  mapLayerId?: string;
  media_type?: string;
}

export interface RelationshipsResponse {
  related_entities: Record<string, RelatedEntityItem[]>;
  geo_entities: Record<string, RelatedEntityItem[]>;
  media: RelatedEntityItem[];
  byTableSlug?: Record<string, RelatedEntityItem[]>;
}

export interface ICmTableDataRelation {
  id: number;
  name: string;
  entity_description: string;
  rel_description: string;
  relationship_id: number;
  is_primary: boolean;
  primary_image: string | null;
  thumbnail: string | null;
  table_slug?: string;
  table_name?: string;
}

export interface ICmTableDataMediaItem {
  id: number;
  name: string;
  entity_description: string;
  rel_description: string;
  relationship_id: number;
  is_primary: boolean;
  media_type: string;
  url: string;
  thumbnail: string;
}

export interface INormalisedRelations {
  /** Related rows grouped by their CmTable.slug. Empty object if none. */
  byTableSlug: Record<string, ICmTableDataRelation[]>;
  /** Media attached to this row. */
  media: ICmTableDataMediaItem[];
}

// ─── Relationship Editor Types ────────────────────────────────────────────────

/** Union of the two item shapes that can appear in the editor state. */
export type RelationshipEditorItem = ICmTableDataRelation | ICmTableDataMediaItem;

/**
 * Working copy of relationships for a single anchor.
 * Keys are table slugs (e.g. "culturalsites") or the literal string "media".
 * Values are the current desired items for that group.
 */
export type RelationshipEditorState = Record<string, RelationshipEditorItem[]>;

/** Converts INormalisedRelations into a RelationshipEditorState. */
export function initRelationshipEditorState(relationships: INormalisedRelations): RelationshipEditorState {
  const state: RelationshipEditorState = {};
  for (const [slug, items] of Object.entries(relationships?.byTableSlug ?? {})) {
    state[slug] = [...items];
  }
  const media = relationships?.media ?? [];
  if (media.length > 0) {
    state["media"] = [...media];
  }
  return state;
}

export interface MediaResponse {
  id: number;
  name: string;
  media_type: "photo" | "video" | "pdf" | string;
  url: string;
  thumbnail: string;
  description: string;
  user: number;
  organisation: number;
  created_at: string;
  last_updated_at: string;
  original_file: {
    filename: string;
    size: string;
    type: string;
  };
  remote_details?: {
    service: "imagekit";
    url: string;
    thumbnail: string;
    id: string;
    mime: string;
  };
  source_folder: string | null;
  relationships?: RelationshipsResponse;
}

interface RelatedItemsRingProps {
  relationships: RelationshipsResponse;
  onItemClick: (item: RelatedEntityItem) => void;
}

export interface MediaItemDetailed {
  id: string | number;
  name: string;
  description: string;
  type: string;
  media_type?: string;
  relationships?: RelationshipsResponse;
  relations?: INormalisedRelations;

  // File information (for upload)
  file?: File;
  fileName?: string;
  previewUrl?: string;

  // URLs (for display)
  url?: string;
  thumbnail?: string;

  // File metadata
  original_file: {
    filename: string;
    type: string;
    size: string;
    hashmd5?: string;
  };

  // Embed URL for iframe-based media (3D models, etc.)
  embed_url?: string;

  // Remote service details (e.g., ImageKit, embed providers)
  remote_details?: {
    service?: string;
    url?: string;
    thumbnail?: string;
    id?: string;
    mime?: string;
    embed_url?: string;
    provider?: string;
  };

  // EXIF metadata extracted from the file at upload time
  exif_metadata?: Record<string, any> | null;
  // Capture location — GeoJSON Point object when set from EXIF, WKT string when returned by API
  geometry?: { type: "Point"; coordinates: [number, number] } | string | null;

  // Allow dynamic string indexing
  [key: string]: any;
}

// Common fields that both types share
export interface CommonFields {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description: string;
  relationships?: RelationshipsResponse;
}

// Helper type for components that just need basic display info
export interface DisplayItem extends CommonFields {
  // URL and thumbnail can be either at top level (MediaItem) or in primary_image (DataItem)
  url?: string;
  thumbnail?: string;
  primary_image?: {
    url?: string | null;
    thumbnail?: string | null;
  };
}

// Create a union type for all possible items
export type UnifiedItem = EntityItem | MediaItem | GeoItem;

// Update DataItemDetailsProps
interface DataItemDetailsProps {
  item: FlexibleDataItem;
  onBack: () => void;
  dataDrawerEndpoint: ApiEndpoint | null; // The endpoint from the drawer
  dataItemEndpoint: ApiEndpoint | null; // The current item's endpoint
  onItemUpdate?: (updatedItem: FlexibleDataItem) => void;
  onItemDelete?: (itemId: number) => void;
}

// Base minimal fields that are always returned from API
export interface MinimalDataItem {
  id: number | string; // Allow both numbers (Django) and strings (Fulcrum UUIDs)
  name: string;
  description?: string;
  thumbnail?: string;
}

// For use in contexts/components that only need minimal data
export interface OptionsData {
  [key: string]: MinimalDataItem[];
}

// Helper functions for presigned URLs
export function isPresignedUrl(url: string): boolean {
  return url.includes("X-Amz-Signature=") && url.includes("Expires=");
}

export function getUrlExpiry(url: string): number | null {
  const match = url.match(/Expires=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

// Add to types.ts if not already present
export interface S3Config {
  region: string;
  bucket: string;
  accessKeyId: string;
  secretAccessKey: string;
}

export interface UploadBatch {
  id: number;
  uuid: string; // Backend returns this but we don't use it for requests
  file_count: number;
  completed_count: number;
  total_size: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  items: Array<{
    id: number;
    name: string;
    status: string;
  }>;
}

export interface OptionsContextType {
  options: OptionsData | null;
  refreshOptions: () => Promise<void>;
  loadOptionsForEndpoint: (endpoints: string | string[]) => Promise<void>;
  isLoading: boolean;
}

// Add this with the other interfaces
export interface UploadError {
  file: string;
  stage: "pre-upload" | "upload" | "post-upload";
  error: string;
}

// Update the existing UploadPhase type
export type UploadPhase = "idle" | "importing" | "uploading" | "complete" | "error";

// Add these types
export interface RelatedEntityItemPost {
  id: number;
  description: string;
}

export interface RelationshipsPost {
  related_entities: Record<string, RelatedEntityItemPost[]>;
  geo_entities: Record<string, RelatedEntityItemPost[]>;
  media: RelatedEntityItemPost[];
}

// ============================================================================
// API PAGINATION TYPES
// ============================================================================

// New paginated API response format
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Helper type for API responses that might be paginated or array
export type ApiResponse<T> = T[] | PaginatedResponse<T>;

// Helper functions for handling API responses
export function extractResultsFromResponse<T>(response: ApiResponse<T>): T[] {
  if (response && typeof response === "object" && "results" in response) {
    return response.results || [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  return [];
}

export function extractPaginationFromResponse<T>(response: ApiResponse<T>): {
  count: number;
  next: string | null;
  previous: string | null;
} | null {
  if (response && typeof response === "object" && "results" in response) {
    return {
      count: response.count,
      next: response.next,
      previous: response.previous
    };
  }
  return null;
}

// ============================================================================
// CENTRALIZED SELECTION SYSTEM TYPES
// ============================================================================

// Keep GeoEntity as an alias for backward compatibility
export type GeoEntity = Entity;

// Utility types for component props that work with centralized selection
export interface SelectionAwareComponentProps {
  selectedEntity?: GeoEntity | null;
  onSelectionChange?: (entity: GeoEntity | null) => void;
}

// Type guard utilities for centralized selection system
export function isGeoEntityWithMedia(entity: GeoEntity): entity is GeoEntity & { relatedMedia: MediaItemDetailed[] } {
  return entity.relatedMedia !== undefined && entity.relatedMedia.length > 0;
}

export function isGeoEntityWithCoordinates(entity: GeoEntity): entity is GeoEntity & { coordinates: [number, number] } {
  return entity.coordinates !== undefined && entity.coordinates.length === 2;
}

export function isGeoEntityWithGeometry(entity: GeoEntity): entity is GeoEntity & { geometry: any } {
  return entity.geometry !== undefined && entity.geometry !== null;
}

// Entity creation helpers for centralized selection
export interface CreateGeoEntityFromFeatureOptions {
  feature: any;
  layerId: string;
  layerConfig?: any;
  entityType?: string;
}

export interface CreateGeoEntityFromItemOptions {
  item: MediaItem | EntityItem;
  endpoint: ApiEndpoint;
  coordinates?: [number, number];
  geometry?: any;
}

// Define a more generic Entity type that can represent ANY entity
export interface Entity {
  id: string | number;
  name: string;
  entityType: string;
  layerId?: string;
  coordinates?: [number, number];
  geometry?: any;
  properties?: Record<string, any>;
  relatedMedia?: MediaItemDetailed[];
  hasMedia?: boolean;
  relationships?: RelationshipsResponse; // Complete relationships object from API
  isComplete?: boolean; // Flag to indicate if full data has been fetched
  // Additional fields that can be at the top level
  thumbnail?: string;
  description?: string;
  primary_image?: PrimaryImage | null;
  created_at?: string;
  last_updated_at?: string;
  url?: string; // For media items
  relations?: INormalisedRelations;
}

// Define types for different filter operations
type FilterType = "intersects" | "contains" | "equals";

export interface FilterOption {
  type: FilterType;
  db_table: string;
  field: string;
  value: string | null;
}
// ============================================================================

// ─── Spatial Dataset Warehouse ───────────────────────────────────────────────

export type SpatialDatasetSourceFormat = "geotiff" | "shapefile" | "gpx" | "geojson" | "kml" | "csv";
export type SpatialDatasetType = "raster" | "vector" | "tabular";
export type SpatialDatasetDisplayType = "cog" | "pmtiles" | "parquet";
export type SpatialDatasetProcessingStatus = "pending" | "uploading" | "queued" | "processing" | "completed" | "failed";

export interface SpatialDatasetAttributeSchema {
  name: string;
  type: "string" | "number" | "boolean";
  sample_values: any[];
}

export interface SpatialDataset {
  id: number;
  uuid: string;
  name: string;
  description: string;
  source_format: SpatialDatasetSourceFormat;
  dataset_type: SpatialDatasetType;
  organisation: number;
  user: number;
  /** S3 key path — now includes /raw/ segment: org_1/spatial/<uuid>/raw/<filename> */
  original_file_key: string;
  original_file: { filename: string } | null;
  display_url: string | null;
  display_type: SpatialDatasetDisplayType | null;
  raster_output: "cog" | "pmtiles" | null;
  processing_status: SpatialDatasetProcessingStatus;
  processing_error: string;
  processing_details: Record<string, any> | null;
  /** Always present on create. Gains signed_url + headers when ?return_signed_url=true */
  upload_details: {
    status: string;
    file_key: string;
    signed_url?: string;
    headers?: Record<string, string>;
  } | null;
  geometry: GeoJSON.Geometry | null;
  /** [minLng, minLat, maxLng, maxLat] */
  bbox: [number, number, number, number] | null;
  source_epsg: number | null;
  bands: { name: string; data_type: string; nodata: number | null }[] | null;
  overviews: number[] | null;
  gsd: number | null;
  attribute_schema: SpatialDatasetAttributeSchema[] | null;
  stac_properties: Record<string, any> | null;
  display_style: Record<string, any>;
  /** Worker-generated thumbnail URL; null until worker sets it (SDW-15) */
  thumbnail: string | null;
  /** Geometry sub-type — set by worker once SDW-19 lands (point|line|polygon) */
  geometry_type?: "point" | "line" | "polygon" | null;
  acquisition_date: string | null;
  time_series_group_id: string | null;
  restrictions: number[];
  created_at: string;
  last_updated_at: string;
  relationships?: { media: any[] };
}

export interface SpatialDatasetCreatePayload {
  name: string;
  source_format: SpatialDatasetSourceFormat;
  dataset_type?: SpatialDatasetType;
  description?: string;
  original_file?: {
    filename: string;
    size: number;
    type: string;
  };
  acquisition_date?: string;
  time_series_group_id?: string;
  display_style?: Record<string, any>;
  restrictions?: number[];
  /** UploadBatch id or UUID — backend auto-increments completed_count on multipart/complete */
  upload_batch?: string | number;
  /**
   * For GeoTIFF multipart uploads: set at create time so multipart/complete auto-triggers
   * the correct output (§7b). For single-PUT GeoTIFF the /process/ call handles this.
   * Ignored by the backend for vector formats.
   */
  raster_output?: "cog" | "pmtiles";
}

export interface SpatialDatasetMultipartInit {
  upload_id: string;
  urls: string[];
  calculation: Record<string, any>;
}

export interface SpatialDatasetMultipartPart {
  PartNumber: number;
  ETag: string;
}

export interface SpatialDatasetUpdatePayload {
  name?: string;
  description?: string;
  display_style?: Record<string, any>;
  acquisition_date?: string | null;
  time_series_group_id?: string | null;
}
